import { useState } from "react";
import { useLocation } from "wouter";
import { Link } from "@/components/SmartLink";
import { SiLinkedin, SiX, SiInstagram } from "react-icons/si";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import logoImage from "@assets/Chief_in_Tech_Global_Logo.png";
import { optimizedImageUrl } from "@/lib/optimized-image";
import { getInjectedBrandLogo } from "@/lib/brandLogos";
import { ProductLogo, type ProductLine } from "@/components/ProductLogos";
import { fundHref, isCapitalDomain } from "@/lib/fundLinks";
import { useQuery } from "@tanstack/react-query";
import type { ProductBrand } from "@shared/schema";
import { 
  Mail, Send, Newspaper, Users, Mic, Tag, PenLine, Handshake, Gift, UserPlus, 
  Search, Building2, MessageSquare, CircleDot, Calendar, Trophy, GraduationCap, 
  TrendingUp, Briefcase, FileCheck, Rocket, BookOpen, Crown, Target, Lightbulb,
  Award, ArrowRight, CheckCircle2, Sparkles, Globe, Heart, Landmark, PlayCircle, type LucideIcon
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { 
  pillarMenuSections, 
  isPathActive,
  type Pillar as NavPillar,
  type NavMenuSection 
} from "@/lib/navigationConfig";
import { cn } from "@/lib/utils";
import { usePlatformTokens } from "@/hooks/usePlatformTokens";
import { useAuth } from "@/hooks/useAuth";
import {
  STARTUP_BENEFITS_PARTNER_CTA_LABEL,
  STARTUP_BENEFITS_PARTNER_PATH,
} from "@shared/startupBenefitsPublic";

type Pillar = "media" | "network" | "community" | "capital" | "book" | "summit" | "default";

const pillarToProduct: Record<Pillar, ProductLine | null> = {
  media: "media",
  network: "network",
  community: null,
  capital: "capital",
  book: "book",
  summit: "summit",
  default: null,
};

interface FooterLinkSection {
  title: string;
  links: { href: string; label: string; icon?: LucideIcon }[];
}

const pillarConfigs: Record<Pillar, FooterLinkSection[]> = {
  media: [
    {
      title: "Content",
      links: [
        { href: "/articles", label: "Articles", icon: Newspaper },
        { href: "/media/daily-brief", label: "Daily Brief", icon: Send },
        { href: "/videos", label: "Video Hub", icon: PlayCircle },
        { href: "/media/news", label: "News Feed", icon: Sparkles },
        { href: "/media/trending", label: "Trending", icon: TrendingUp },
        { href: "/media/topics", label: "Topics", icon: Tag },
      ],
    },
    {
      title: "Discover",
      links: [
        { href: "/media/news/funding", label: "Funding News", icon: TrendingUp },
        { href: "/media/news/executive-moves", label: "Executive Moves", icon: Briefcase },
        { href: "/leaders", label: "100 Leaders List", icon: Award },
        { href: "/podcasts", label: "Podcast", icon: Mic },
      ],
    },
    {
      title: "Contribute",
      links: [
        { href: "/contributor/pitch", label: "Submit a Pitch", icon: PenLine },
        { href: "/contributor", label: "Contributor Dashboard", icon: FileCheck },
        { href: "/media/advertise", label: "Advertise With Us", icon: Target },
      ],
    },
  ],
  network: [
    {
      title: "Connect",
      links: [
        { href: "/directory", label: "Profile Directory", icon: Users },
        { href: "/circles", label: "Peer Learning Circles", icon: CircleDot },
        { href: "/events", label: "Events Calendar", icon: Calendar },
        { href: "/network/intro-requests", label: "Warm Introductions", icon: UserPlus },
      ],
    },
    {
      title: "Opportunities",
      links: [
        { href: "/executive-search", label: "Executive Search", icon: Search },
        { href: "/board-match", label: "Board Match", icon: Crown },
        { href: "/network/office-hours", label: "EWIT Office Hours", icon: Calendar },
        { href: "/jobs", label: "Executive Job Board", icon: Briefcase },
      ],
    },
    {
      title: "Grow",
      links: [
        { href: "/ecosystem", label: "Ecosystem Map", icon: Globe },
        { href: "/advisors", label: "Find Advisors", icon: Lightbulb },
        { href: "/network/perks", label: "Partner Perks", icon: Gift },
        { href: "/partners", label: "Partner With Us", icon: Handshake },
      ],
    },
  ],
  community: [
    {
      title: "Engage",
      links: [
        { href: "/circles", label: "Peer Learning Circles", icon: CircleDot },
        { href: "/events", label: "Events Calendar", icon: Calendar },
        { href: "/community/ask-board", label: "Ask the Board", icon: MessageSquare },
        { href: "/get-involved", label: "Get Involved", icon: Heart },
      ],
    },
    {
      title: "Learn & Compete",
      links: [
        { href: "/academy", label: "Academy", icon: GraduationCap },
        { href: "/challenges", label: "Innovation Challenges", icon: Trophy },
        { href: "/cohorts/apply", label: "Cohort Programs", icon: Users },
        { href: "/ecosystem", label: "Ecosystem Hubs", icon: Globe },
      ],
    },
    {
      title: "Events",
      links: [
        { href: "/events/chief-in-tech-summit", label: "Chief in Tech Summit", icon: Building2 },
        { href: "/events/executive-forum", label: "Executive Forum Series", icon: Award },
        { href: "/speakers", label: "Speakers", icon: Mic },
      ],
    },
  ],
  capital: [
    {
      title: "Explore",
      links: [
        { href: "/capital/fund/thesis", label: "Thesis", icon: Target },
        { href: "/capital/fund/portfolio", label: "Portfolio", icon: TrendingUp },
        { href: "/capital/fund/for-founders", label: "For Founders", icon: Rocket },
        { href: "/startup-benefits", label: "Startup Benefits", icon: Gift },
      ],
    },
    {
      title: "Connect",
      links: [
        { href: "/capital/fund/community", label: "Community", icon: Users },
        { href: "/capital/fund/events", label: "Events", icon: Calendar },
        { href: "/capital/fund/contact", label: "Contact", icon: Mail },
      ],
    },
    {
      title: "Participate",
      links: [
        { href: "/capital/fund/apply/founder", label: "Pitch Your Startup", icon: Rocket },
        { href: "/capital/fund/deal-readiness", label: "Deal Readiness tool (sign in)", icon: FileCheck },
        { href: "/capital/fund/support-lps", label: "For LPs", icon: Landmark },
        { href: STARTUP_BENEFITS_PARTNER_PATH, label: STARTUP_BENEFITS_PARTNER_CTA_LABEL, icon: Handshake },
      ],
    },
  ],
  book: [
    {
      title: "Chief in Tech Book",
      links: [
        { href: "/book", label: "Overview", icon: BookOpen },
        { href: "/book#reviews", label: "Reviews & Endorsements", icon: MessageSquare },
        { href: "/book#author", label: "About the Author", icon: Users },
        { href: "/book#worldwide", label: "Buy Worldwide", icon: Globe },
      ],
    },
    {
      title: "More",
      links: [
        { href: "/book/speaker-request", label: "Book Anna as Speaker", icon: Mic },
        { href: "/books", label: "Books Library", icon: BookOpen },
        { href: "/book#speaking", label: "Speaking Engagements", icon: Award },
      ],
    },
  ],
  summit: [
    {
      title: "Chief in Tech Summit",
      links: [
        { href: "/events/chief-in-tech-summit", label: "Summit Overview", icon: Calendar },
        { href: "/speakers", label: "Speakers", icon: Mic },
        { href: "/summit/shop", label: "Summit Shop", icon: Rocket },
      ],
    },
    {
      title: "More Events",
      links: [
        { href: "/events/executive-forum", label: "Executive Forum Series", icon: Award },
        { href: "/events", label: "All Events", icon: Calendar },
        { href: "/magazine", label: "Magazine", icon: BookOpen },
      ],
    },
  ],
  default: [
    {
      title: "Platform",
      links: [
        { href: "/media", label: "Media Hub", icon: Newspaper },
        { href: "/directory", label: "Network Directory", icon: Users },
        { href: "/capital", label: "Capital", icon: TrendingUp },
        { href: "/events", label: "Events", icon: Calendar },
      ],
    },
    {
      title: "Discover",
      links: [
        { href: "/leaders", label: "100 Leaders List", icon: Award },
        { href: "/magazine", label: "Magazine", icon: BookOpen },
        { href: "/book", label: "Chief in Tech Book", icon: BookOpen },
        { href: "/ecosystem", label: "Ecosystem Map", icon: Globe },
      ],
    },
    {
      title: "Join & Learn",
      links: [
        { href: "/events/chief-in-tech-summit", label: "Summit", icon: Building2 },
        { href: "/academy", label: "Academy", icon: GraduationCap },
        { href: "/get-involved", label: "Get Involved", icon: Heart },
        { href: "/videos", label: "Video Hub", icon: PlayCircle },
      ],
    },
  ],
};

const roleLinks = [
  { href: "/join/executive", label: "For Executives", description: "Board seats & executive networking", icon: Crown },
  { href: "/join/founder", label: "For Founders", description: "Raise capital & find advisors", icon: Rocket },
  { href: "/join/investor", label: "For Investors", description: "Access vetted deal flow", icon: TrendingUp },
  { href: "/join/advisor", label: "For Advisors", description: "Guide the next generation", icon: Lightbulb },
];

const outcomeLinks = [
  { href: "/capital/deal-readiness", label: "Raise Capital", description: "Get investor-ready", icon: TrendingUp },
  { href: "/advisors", label: "Find Advisors", description: "Connect with experts", icon: Search },
  { href: "/speakers", label: "Book Speakers", description: "World-class talent", icon: Mic },
  { href: "/academy", label: "Upskill Teams", description: "Leadership courses", icon: GraduationCap },
];

const mediaCategoryLinks = [
  { href: "/articles?category=leadership", label: "Tech Leadership Stories" },
  { href: "/articles?category=funding", label: "Founder Funding News" },
  { href: "/articles?category=innovation", label: "Tech Innovation Insights" },
  { href: "/articles?category=career", label: "Executive Career Strategies" },
];

const networkCategoryLinks = [
  { href: "/network/office-hours", label: "EWIT Office Hours Marketplace" },
  { href: "/network/intro-requests", label: "Warm Introduction Platform" },
  { href: "/network/perks", label: "Exclusive Partner Benefits" },
  { href: "/directory", label: "Profile Directory" },
  { href: "/jobs", label: "Executive Job Board" },
  { href: "/board-opportunities", label: "Board Opportunities" },
];

const communityCategoryLinks = [
  { href: "/circles", label: "Executive Mastermind Groups" },
  { href: "/events", label: "Tech Leadership Events Calendar" },
  { href: "/challenges", label: "Innovation Challenge Programs" },
  { href: "/community/ask-board", label: "Peer Advisory Board" },
  { href: "/ecosystem", label: "Ecosystem Hubs" },
  { href: "/cohorts/apply", label: "Cohort Programs" },
];

const capitalCategoryLinks = [
  { href: "/capital/dealroom", label: "Founder Dealroom Insights" },
  { href: "/capital/deal-readiness", label: "Investor Readiness Assessment" },
  { href: "/capital/investor-apply", label: "Vetted Investor Network Access" },
  { href: "/investor-network", label: "Investor Network Clubs" },
  { href: "/founder/workspace", label: "Founder Capital Toolkit" },
  { href: "/funding", label: "Funding Rounds & News" },
];


const companyLinks = [
  { href: "/about", label: "About Us", adminOnly: false },
  { href: "/our-team", label: "Our Team", adminOnly: true },
  { href: "/careers", label: "Careers", adminOnly: true },
  { href: "/contact", label: "Contact", adminOnly: false },
  { href: "/press", label: "Press", adminOnly: false },
  { href: "/brand-hub", label: "Brand Hub", adminOnly: false },
];

const legalLinks = [
  { href: "/privacy", label: "Privacy Policy" },
  { href: "/terms", label: "Terms of Service" },
  { href: "/cookies", label: "Cookie Policy" },
  { href: "/accessibility", label: "Accessibility" },
];

const socialLinks = [
  { href: "https://www.linkedin.com/company/chief-in-tech-global-media", label: "LinkedIn", icon: SiLinkedin },
  { href: "https://x.com/WomenTechNet", label: "X (Twitter)", icon: SiX },
  { href: "https://www.instagram.com/womentechnet", label: "Instagram", icon: SiInstagram },
];

function detectPillar(pathname: string): Pillar {
  // The capital subdomain (chiefintech.capital) serves /capital/fund/* under
  // the canonical short URLs /fund/*. Anything on that domain — and any
  // /fund/* path even on the main domain — belongs to the capital pillar.
  if (isCapitalDomain() || pathname === "/fund" || pathname.startsWith("/fund/")) {
    return "capital";
  }
  if (pathname.startsWith("/media") || pathname.startsWith("/articles") || pathname.startsWith("/content") || pathname.startsWith("/contributor") || pathname.startsWith("/leaders") || pathname.startsWith("/podcasts") || pathname.startsWith("/topics")) {
    return "media";
  }
  if (pathname.startsWith("/network") || pathname.startsWith("/advisors") || pathname.startsWith("/directory") || pathname.startsWith("/founders") || pathname.startsWith("/jobs") || pathname.startsWith("/executive-search") || pathname.startsWith("/board-match")) {
    return "network";
  }
  if (pathname.startsWith("/community") || pathname.startsWith("/circles") || pathname.startsWith("/events") || pathname.startsWith("/challenges") || pathname.startsWith("/academy")) {
    return "community";
  }
  if (pathname.startsWith("/capital") || pathname.startsWith("/founder") || pathname.startsWith("/dealroom") || pathname.startsWith("/investor")) {
    return "capital";
  }
  if (pathname.startsWith("/book")) {
    return "book";
  }
  if (pathname.startsWith("/summit") || pathname === "/events/chief-in-tech-summit") {
    return "summit";
  }
  return "default";
}

function NewsletterSignup({ compact = false }: { compact?: boolean }) {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setIsSubmitting(true);
    try {
      const response = await fetch("/api/newsletter/subscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast({
          title: "Subscribed!",
          description: "You've been added to our newsletter.",
        });
        setEmail("");
      } else {
        toast({
          title: "Error",
          description: data.message || "Something went wrong. Please try again.",
          variant: "destructive",
        });
      }
    } catch {
      toast({
        title: "Error",
        description: "Something went wrong. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className={compact ? "mt-3" : "mt-4"}>
      {!compact && (
        <p className="text-sm text-muted-foreground mb-3">
          Get the latest insights on tech leadership delivered to your inbox.
        </p>
      )}
      <div className="flex gap-2">
        <Input
          type="email"
          placeholder="Enter your email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="flex-1"
          data-testid="input-newsletter-email"
          aria-label="Email for newsletter"
          required
        />
        <Button type="submit" disabled={isSubmitting} data-testid="button-newsletter-submit">
          <Send className="h-4 w-4" />
          <span className="sr-only">Subscribe</span>
        </Button>
      </div>
    </form>
  );
}

function HomepageSchemaMarkup() {
  const { platformStats } = usePlatformTokens();
  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": "https://chiefintech.com/#organization",
        name: "Chief in Tech",
        description: "Where executives, founders, investors, and board candidates shape the future of technology — through WomenTech Network peer circles, Executive Women in Tech (EWIT) advisory services, and Chief in Tech Media & Capital.",
        url: "https://chiefintech.com",
        logo: {
          "@type": "ImageObject",
          url: "https://chiefintech.com/logo.png",
          width: 200,
          height: 60,
        },
        sameAs: [
          "https://www.linkedin.com/company/chief-in-tech-global-media",
          "https://x.com/WomenTechNet",
          "https://www.instagram.com/womentechnet",
        ],
        contactPoint: {
          "@type": "ContactPoint",
          contactType: "customer service",
          email: "hello@chiefintech.com",
        },
        foundingDate: "2020",
        numberOfEmployees: {
          "@type": "QuantitativeValue",
          minValue: 10,
          maxValue: 50,
        },
      },
      {
        "@type": "WebSite",
        "@id": "https://chiefintech.com/#website",
        url: "https://chiefintech.com",
        name: "Chief in Tech",
        description: "Connecting and empowering emerging leaders in technology",
        publisher: { "@id": "https://chiefintech.com/#organization" },
        potentialAction: {
          "@type": "SearchAction",
          target: "https://chiefintech.com/search?q={search_term_string}",
          "query-input": "required name=search_term_string",
        },
      },
      {
        "@type": "WebPage",
        "@id": "https://chiefintech.com/#webpage",
        url: "https://chiefintech.com",
        name: "Chief in Tech - Empowering Tech Leaders",
        isPartOf: { "@id": "https://chiefintech.com/#website" },
        about: { "@id": "https://chiefintech.com/#organization" },
        description: `Join ${platformStats.techLeaders.value} executives, founders, and investors shaping the future of technology. Access exclusive networking, capital connections, and leadership resources.`,
      },
      {
        "@type": "SiteNavigationElement",
        "@id": "https://chiefintech.com/#navigation",
        name: "Main Navigation",
        hasPart: [
          { "@type": "SiteNavigationElement", name: "Discover", url: "https://chiefintech.com/media" },
          { "@type": "SiteNavigationElement", name: "Network", url: "https://chiefintech.com/directory" },
          { "@type": "SiteNavigationElement", name: "Capital", url: "https://chiefintech.com/capital" },
          { "@type": "SiteNavigationElement", name: "Programs", url: "https://chiefintech.com/academy" },
          { "@type": "SiteNavigationElement", name: "Membership", url: "https://chiefintech.com/membership" },
        ],
      },
      {
        "@type": "BreadcrumbList",
        "@id": "https://chiefintech.com/#breadcrumb",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://chiefintech.com" },
        ],
      },
    ],
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

function StandardSchemaMarkup() {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Chief in Tech",
    description: "Where executives, founders, investors, and board candidates shape the future of technology — through WomenTech Network peer circles, Executive Women in Tech (EWIT) advisory services, and Chief in Tech Media & Capital.",
    url: "https://chiefintech.com",
    logo: "https://chiefintech.com/logo.png",
    sameAs: [
      "https://www.linkedin.com/company/chief-in-tech-global-media",
      "https://x.com/WomenTechNet",
      "https://www.instagram.com/womentechnet",
    ],
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "customer service",
      email: "hello@chiefintech.com",
    },
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

function HomepageFooter({ currentYear, globalLogoUrl }: { currentYear: number; globalLogoUrl: string }) {
  const { user } = useAuth();
  const { platformStats } = usePlatformTokens();

  const trustSignals = [
    { metric: platformStats.techLeaders.value, label: "Tech Leaders" },
    { metric: platformStats.capitalConnected.value, label: "Capital Connected" },
    { metric: platformStats.countries.value, label: "Countries" },
  ];

  return (
    <>
      <HomepageSchemaMarkup />
      <footer
        className="border-t bg-muted/30"
        role="contentinfo"
        aria-label="Site footer"
        data-testid="footer"
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="py-12 lg:py-16">
            <div className="grid grid-cols-1 gap-12 lg:gap-8">
              <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
                <div className="sm:col-span-2 lg:col-span-1">
                  <Link href="/" data-testid="link-footer-logo">
                    <img src={optimizedImageUrl(globalLogoUrl, 280)} alt="Chief in Tech Global" width={140} height={36} className="h-10 w-auto dark:brightness-0 dark:invert" />
                  </Link>
                  <p className="mt-4 text-sm text-muted-foreground">
                    A global platform for tech leadership, media, capital, and programs. Leadership programs and memberships are operated by EWIT through WomenTech Network.
                  </p>
                  <div className="mt-6 flex gap-4">
                    {trustSignals.map((signal) => (
                      <div key={signal.label} className="text-center">
                        <p className="text-lg font-bold text-primary" data-testid={`text-trust-${signal.label.toLowerCase().replace(/\s+/g, "-")}`}>
                          {signal.metric}
                        </p>
                        <p className="text-xs text-muted-foreground">{signal.label}</p>
                      </div>
                    ))}
                  </div>
                  <div className="mt-6" data-testid="section-ecosystem">
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3" data-testid="text-ecosystem-heading">
                      Ecosystem
                    </p>
                    <div className="flex flex-wrap gap-3">
                      {(["capital", "media", "network", "summit", "book", "chiefinai"] as ProductLine[]).map((product) => (
                        <Link 
                          key={product} 
                          href={product === "chiefinai" ? "https://www.chiefinai.com" : `/${product}`}
                          data-testid={`link-ecosystem-${product}`}
                        >
                          <span className="text-muted-foreground transition-colors hover:text-foreground">
                            <ProductLogo product={product} variant="compact" size="sm" />
                          </span>
                        </Link>
                      ))}
                    </div>
                  </div>
                  <div className="mt-6 flex gap-2">
                    {socialLinks.map((social) => (
                      <Button key={social.href} variant="ghost" size="icon" asChild>
                        <a
                          href={social.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={`Follow us on ${social.label}`}
                          data-testid={`link-social-${social.label.toLowerCase().replace(/[^a-z]/g, "")}`}
                        >
                          <social.icon className="h-4 w-4" />
                        </a>
                      </Button>
                    ))}
                  </div>
                </div>

                <nav aria-label="Discover by role">
                  <h3 className="font-heading text-sm font-semibold uppercase tracking-wider" data-testid="text-footer-role-heading">
                    Join By Role
                  </h3>
                  <ul className="mt-4 space-y-3" role="list">
                    {roleLinks.map((link) => (
                      <li key={link.href}>
                        <Link href={link.href}>
                          <span
                            className="group flex items-start gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
                            data-testid={`link-footer-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                          >
                            <link.icon className="h-4 w-4 mt-0.5 shrink-0" />
                            <span>
                              <span className="font-medium text-foreground">{link.label}</span>
                              <span className="block text-xs">{link.description}</span>
                            </span>
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </nav>

                <nav aria-label="Explore by outcome">
                  <h3 className="font-heading text-sm font-semibold uppercase tracking-wider" data-testid="text-footer-outcome-heading">
                    What You Can Do
                  </h3>
                  <ul className="mt-4 space-y-3" role="list">
                    {outcomeLinks.map((link) => (
                      <li key={link.href}>
                        <Link href={link.href}>
                          <span
                            className="group flex items-start gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
                            data-testid={`link-footer-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                          >
                            <link.icon className="h-4 w-4 mt-0.5 shrink-0" />
                            <span>
                              <span className="font-medium text-foreground">{link.label}</span>
                              <span className="block text-xs">{link.description}</span>
                            </span>
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </nav>

                <div>
                  <h3 className="font-heading text-sm font-semibold uppercase tracking-wider" data-testid="text-footer-newsletter-heading">
                    Stay Connected
                  </h3>
                  <p className="mt-4 text-sm text-muted-foreground">
                    Weekly insights on tech leadership, funding news, and exclusive opportunities.
                  </p>
                  <NewsletterSignup compact />
                  <div className="mt-4 flex flex-wrap gap-2">
                    <Badge variant="secondary" className="text-xs">
                      <CheckCircle2 className="h-3 w-3 mr-1" />
                      Free forever
                    </Badge>
                    <Badge variant="secondary" className="text-xs">
                      <Sparkles className="h-3 w-3 mr-1" />
                      AI-curated
                    </Badge>
                  </div>
                </div>
              </div>

              <div className="border-t pt-8">
                <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
                  <nav aria-label="Media content categories">
                    <h4 className="flex items-center gap-2 font-heading text-sm font-semibold" data-testid="text-footer-media-categories">
                      <Newspaper className="h-4 w-4" />
                      Media
                    </h4>
                    <ul className="mt-3 space-y-2" role="list">
                      {mediaCategoryLinks.map((link) => (
                        <li key={link.href}>
                          <Link href={link.href}>
                            <span className="text-xs text-muted-foreground transition-colors hover:text-foreground" data-testid={`link-footer-${link.label.toLowerCase().replace(/\s+/g, "-").substring(0, 30)}`}>
                              {link.label}
                            </span>
                          </Link>
                        </li>
                      ))}
                      <li>
                        <Link href="/media">
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-primary transition-colors hover:text-primary/80">
                            Explore all content <ArrowRight className="h-3 w-3" />
                          </span>
                        </Link>
                      </li>
                    </ul>
                  </nav>

                  <nav aria-label="Network categories">
                    <h4 className="flex items-center gap-2 font-heading text-sm font-semibold" data-testid="text-footer-network-categories">
                      <Users className="h-4 w-4" />
                      Network
                    </h4>
                    <ul className="mt-3 space-y-2" role="list">
                      {networkCategoryLinks.map((link) => (
                        <li key={link.href}>
                          <Link href={link.href}>
                            <span className="text-xs text-muted-foreground transition-colors hover:text-foreground" data-testid={`link-footer-${link.label.toLowerCase().replace(/\s+/g, "-").substring(0, 30)}`}>
                              {link.label}
                            </span>
                          </Link>
                        </li>
                      ))}
                      <li>
                        <Link href="/network">
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-primary transition-colors hover:text-primary/80">
                            Browse network <ArrowRight className="h-3 w-3" />
                          </span>
                        </Link>
                      </li>
                    </ul>
                  </nav>

                  <nav aria-label="Community categories">
                    <h4 className="flex items-center gap-2 font-heading text-sm font-semibold" data-testid="text-footer-community-categories">
                      <Heart className="h-4 w-4" />
                      Community
                    </h4>
                    <ul className="mt-3 space-y-2" role="list">
                      {communityCategoryLinks.map((link) => (
                        <li key={link.href}>
                          <Link href={link.href}>
                            <span className="text-xs text-muted-foreground transition-colors hover:text-foreground" data-testid={`link-footer-${link.label.toLowerCase().replace(/\s+/g, "-").substring(0, 30)}`}>
                              {link.label}
                            </span>
                          </Link>
                        </li>
                      ))}
                      <li>
                        <Link href="/community">
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-primary transition-colors hover:text-primary/80">
                            Join community <ArrowRight className="h-3 w-3" />
                          </span>
                        </Link>
                      </li>
                    </ul>
                  </nav>

                  <nav aria-label="Capital categories">
                    <h4 className="flex items-center gap-2 font-heading text-sm font-semibold" data-testid="text-footer-capital-categories">
                      <TrendingUp className="h-4 w-4" />
                      Capital
                    </h4>
                    <ul className="mt-3 space-y-2" role="list">
                      {capitalCategoryLinks.map((link) => (
                        <li key={link.href}>
                          <Link href={link.href}>
                            <span className="text-xs text-muted-foreground transition-colors hover:text-foreground" data-testid={`link-footer-${link.label.toLowerCase().replace(/\s+/g, "-").substring(0, 30)}`}>
                              {link.label}
                            </span>
                          </Link>
                        </li>
                      ))}
                      <li>
                        <Link href="/capital">
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-primary transition-colors hover:text-primary/80">
                            Access capital <ArrowRight className="h-3 w-3" />
                          </span>
                        </Link>
                      </li>
                    </ul>
                  </nav>
                </div>
              </div>

              <div className="border-t pt-8">
                <div className="grid grid-cols-2 gap-8 sm:grid-cols-3 lg:grid-cols-6">
                  <nav aria-label="Quick links">
                    <h4 className="font-heading text-sm font-semibold" data-testid="text-footer-quick-links">Quick Links</h4>
                    <ul className="mt-3 space-y-2" role="list">
                      <li><Link href="/summit"><span className="text-xs text-muted-foreground hover:text-foreground" data-testid="link-footer-summit">Summit 2026</span></Link></li>
                      <li><Link href="/leaders"><span className="text-xs text-muted-foreground hover:text-foreground" data-testid="link-footer-leaders">100 Leaders List</span></Link></li>
                      <li><Link href="/book"><span className="text-xs text-muted-foreground hover:text-foreground" data-testid="link-footer-book">The Book</span></Link></li>
                      <li><Link href="/podcasts"><span className="text-xs text-muted-foreground hover:text-foreground" data-testid="link-footer-podcast">Podcast</span></Link></li>
                      {user?.isAdmin && (
                        <li><Link href="/ecosystem"><span className="inline-flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400 hover:text-amber-700 dark:hover:text-amber-300 font-medium" data-testid="link-footer-ecosystem">Ecosystem Map</span></Link></li>
                      )}
                      <li><Link href="/jobs"><span className="text-xs text-muted-foreground hover:text-foreground" data-testid="link-footer-jobs">Job Board</span></Link></li>
                      <li><Link href="/board-opportunities"><span className="text-xs text-muted-foreground hover:text-foreground" data-testid="link-footer-board-opportunities">Board Opportunities</span></Link></li>
                    </ul>
                  </nav>

                  <nav aria-label="For subscribers">
                    <h4 className="font-heading text-sm font-semibold" data-testid="text-footer-subscribers">For Subscribers</h4>
                    <ul className="mt-3 space-y-2" role="list">
                      <li><Link href="/home"><span className="text-xs text-muted-foreground hover:text-foreground" data-testid="link-footer-dashboard">Dashboard</span></Link></li>
                      <li><Link href="/profile"><span className="text-xs text-muted-foreground hover:text-foreground" data-testid="link-footer-profile">My Profile</span></Link></li>
                      <li><Link href="/account"><span className="text-xs text-muted-foreground hover:text-foreground" data-testid="link-footer-settings">Settings</span></Link></li>
                      <li><Link href="/membership"><span className="text-xs text-muted-foreground hover:text-foreground" data-testid="link-footer-membership">Membership</span></Link></li>
                    </ul>
                  </nav>

                  <nav aria-label="Company links">
                    <h4 className="font-heading text-sm font-semibold" data-testid="text-footer-company-heading">Company</h4>
                    <ul className="mt-3 space-y-2" role="list">
                      {companyLinks.filter(link => !link.adminOnly || user?.isAdmin).map((link) => (
                        <li key={link.href}>
                          <Link href={link.href}>
                            <span className={link.adminOnly ? "inline-flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400 hover:text-amber-700 dark:hover:text-amber-300 font-medium" : "text-xs text-muted-foreground hover:text-foreground"} data-testid={`link-footer-${link.label.toLowerCase().replace(/\s+/g, "-")}`}>
                              {link.label}
                            </span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </nav>

                  <nav aria-label="Resources">
                    <h4 className="font-heading text-sm font-semibold" data-testid="text-footer-resources">Resources</h4>
                    <ul className="mt-3 space-y-2" role="list">
                      <li><Link href="/help-center"><span className="text-xs text-muted-foreground hover:text-foreground" data-testid="link-footer-help-center">Help Center</span></Link></li>
                      <li><Link href="/faq"><span className="text-xs text-muted-foreground hover:text-foreground" data-testid="link-footer-faqs">FAQs</span></Link></li>
                      <li><Link href="/blog"><span className="text-xs text-muted-foreground hover:text-foreground" data-testid="link-footer-blog">Articles</span></Link></li>
                      <li><Link href="/newsletter"><span className="text-xs text-muted-foreground hover:text-foreground" data-testid="link-footer-newsletter">Newsletter</span></Link></li>
                      {user?.isAdmin && (
                        <li><Link href="/pricing"><span className="inline-flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400 hover:text-amber-700 dark:hover:text-amber-300 font-medium" data-testid="link-footer-pricing"><Badge variant="outline" className="text-[10px] px-1 py-0 border-amber-500 text-amber-600 dark:text-amber-400">Needs Update</Badge>Pricing</span></Link></li>
                      )}
                      {user?.isAdmin && (
                        <li><Link href="/contribute"><span className="inline-flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400 hover:text-amber-700 dark:hover:text-amber-300 font-medium" data-testid="link-footer-contribute">Contribute</span></Link></li>
                      )}
                    </ul>
                  </nav>

                  <nav aria-label="Partners">
                    <h4 className="font-heading text-sm font-semibold" data-testid="text-footer-partners">Partners</h4>
                    <ul className="mt-3 space-y-2" role="list">
                      <li><Link href="/partners"><span className="text-xs text-muted-foreground hover:text-foreground" data-testid="link-footer-become-partner">Become a Partner</span></Link></li>
                      <li><Link href="/sponsorships"><span className="text-xs text-muted-foreground hover:text-foreground" data-testid="link-footer-sponsorships">Sponsorships</span></Link></li>
                      <li><Link href="/advertise"><span className="text-xs text-muted-foreground hover:text-foreground" data-testid="link-footer-advertise">Advertise</span></Link></li>
                      {user?.isAdmin && (
                        <li><Link href="/affiliates"><span className="inline-flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400 hover:text-amber-700 dark:hover:text-amber-300 font-medium" data-testid="link-footer-affiliates">Affiliates</span></Link></li>
                      )}
                    </ul>
                  </nav>

                  <nav aria-label="Legal links">
                    <h4 className="font-heading text-sm font-semibold" data-testid="text-footer-legal">Legal</h4>
                    <ul className="mt-3 space-y-2" role="list">
                      {legalLinks.map((link) => (
                        <li key={link.href}>
                          <Link href={link.href}>
                            <span className="text-xs text-muted-foreground hover:text-foreground" data-testid={`link-footer-${link.label.toLowerCase().replace(/\s+/g, "-")}`}>
                              {link.label}
                            </span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </nav>
                </div>
              </div>
            </div>
          </div>

          <div className="border-t py-6">
            <div className="flex flex-col items-center justify-between gap-4 sm:flex-row flex-wrap">
              <div className="flex flex-wrap items-center gap-4">
                <p className="text-xs text-muted-foreground" data-testid="text-copyright">
                  &copy; {currentYear} Chief in Tech LLC. All rights reserved.
                </p>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Globe className="h-3 w-3" />
                  <span>Global Platform</span>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                Empowering women leaders in technology worldwide
              </p>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
}

function StandardFooter({ currentYear, pillar, globalLogoUrl }: { currentYear: number; pillar: Pillar; globalLogoUrl: string }) {
  const [location] = useLocation();
  const { user } = useAuth();
  const brandProduct = pillarToProduct[pillar];
  
  const menuSections: NavMenuSection[] = pillar !== "capital" && pillar !== "default" && pillar in pillarMenuSections
    ? pillarMenuSections[pillar as NavPillar] 
    : [];
  
  const fallbackSections = pillarConfigs[pillar] || pillarConfigs.default;
  const hasPillarSections = menuSections.length > 0;
  const footerCompanyLinks = pillar === "capital"
    ? [
        { href: "/capital/fund/team", label: "Team", adminOnly: false },
        { href: "/capital/fund/advisory-board", label: "Advisory Board", adminOnly: false },
        { href: "/capital/fund/insights", label: "Insights", adminOnly: false },
        { href: "/capital/fund/risk-factors", label: "Risk Factors", adminOnly: false },
      ]
    : companyLinks;
  
  return (
    <>
      {pillar !== "capital" && <StandardSchemaMarkup />}
      <footer
        className="border-t bg-muted/30"
        role="contentinfo"
        aria-label="Site footer"
        data-testid="footer"
      >
        {brandProduct && pillar !== "capital" && (
          <div className="bg-gradient-to-r from-[#4C1481]/5 to-transparent border-b">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-4">
              <ProductLogo product={brandProduct} variant="full" size="md" />
            </div>
          </div>
        )}
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="py-12 lg:py-16">
            <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-6">
              <div className="lg:col-span-2">
                <Link href={pillar === "capital" ? "/capital/fund" : "/"} data-testid="link-footer-logo">
                  {pillar === "capital" ? (
                    <ProductLogo product="capital" variant="full" size="md" />
                  ) : (
                    <img src={optimizedImageUrl(globalLogoUrl, 280)} alt="Chief in Tech Global" width={140} height={36} className="h-10 w-auto dark:brightness-0 dark:invert" />
                  )}
                </Link>
                <p className="mt-4 max-w-xs text-sm text-muted-foreground">
                  {pillar === "capital"
                    ? "Backing operators-turned-founders and AI builders creating enterprise systems at pre-seed and seed."
                    : "Where executives, founders, investors, and board candidates shape the future of technology — through WomenTech Network peer circles, Executive Women in Tech (EWIT) advisory services, and Chief in Tech Media & Capital."}
                </p>
                {pillar !== "capital" && (
                  <>
                    <div className="mt-6 flex gap-2" role="list" aria-label="Social media links">
                      {socialLinks.map((social) => (
                        <Button key={social.href} variant="ghost" size="icon" asChild>
                          <a
                            href={social.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label={`Follow us on ${social.label}`}
                            data-testid={`link-social-${social.label.toLowerCase().replace(/[^a-z]/g, "")}`}
                          >
                            <social.icon className="h-4 w-4" />
                          </a>
                        </Button>
                      ))}
                    </div>
                    <NewsletterSignup />
                  </>
                )}
              </div>

              {hasPillarSections ? (
                menuSections.map((section) => (
                  <nav key={section.title} aria-label={`${section.title} navigation`}>
                    <h3
                      className="font-heading text-sm font-semibold uppercase tracking-wider"
                      data-testid={`text-footer-${(section.title || '').toLowerCase().replace(/\s+/g, "-")}-heading`}
                    >
                      {section.title}
                    </h3>
                    <ul className="mt-4 space-y-3" role="list">
                      {section.items.map((item) => {
                        const isActive = isPathActive(location, item.href);
                        return (
                          <li key={item.href}>
                            <Link href={item.href}>
                              <span
                                className={cn(
                                  "inline-flex items-center gap-2 text-sm transition-colors hover:text-foreground",
                                  isActive 
                                    ? "text-foreground font-medium" 
                                    : "text-muted-foreground"
                                )}
                                data-testid={`link-footer-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                                data-active={isActive}
                              >
                                {item.icon && <item.icon className={cn("h-3.5 w-3.5", isActive && "text-primary")} />}
                                {item.label}
                              </span>
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  </nav>
                ))
              ) : (
                fallbackSections.map((section) => (
                  <nav key={section.title} aria-label={`${section.title} navigation`}>
                    <h3
                      className="font-heading text-sm font-semibold uppercase tracking-wider"
                      data-testid={`text-footer-${section.title.toLowerCase().replace(/\s+/g, "-")}-heading`}
                    >
                      {section.title}
                    </h3>
                    <ul className="mt-4 space-y-3" role="list">
                      {section.links.map((link) => {
                        const isActive = isPathActive(location, link.href);
                        return (
                          <li key={link.href}>
                            <Link href={link.href}>
                              <span
                                className={cn(
                                  "inline-flex items-center gap-2 text-sm transition-colors hover:text-foreground",
                                  isActive 
                                    ? "text-foreground font-medium" 
                                    : "text-muted-foreground"
                                )}
                                data-testid={`link-footer-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                                data-active={isActive}
                              >
                                {link.icon && <link.icon className={cn("h-3.5 w-3.5", isActive && "text-primary")} />}
                                {link.label}
                              </span>
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  </nav>
                ))
              )}

              <nav aria-label={pillar === "capital" ? "About Chief in Tech Capital navigation" : "Company navigation"}>
                <h3
                  className="font-heading text-sm font-semibold uppercase tracking-wider"
                  data-testid="text-footer-company-heading"
                >
                  {pillar === "capital" ? "About" : "Company"}
                </h3>
                <ul className="mt-4 space-y-3" role="list">
                  {footerCompanyLinks.filter(link => !link.adminOnly || user?.isAdmin).slice(0, 4).map((link) => {
                    const isActive = isPathActive(location, link.href);
                    return (
                      <li key={link.href}>
                        <Link href={link.href}>
                          <span
                            className={cn(
                              "text-sm transition-colors hover:text-foreground",
                              link.adminOnly
                                ? "text-amber-600 dark:text-amber-400 hover:text-amber-700 dark:hover:text-amber-300 font-medium"
                                : isActive 
                                  ? "text-foreground font-medium" 
                                  : "text-muted-foreground"
                            )}
                            data-testid={`link-footer-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                            data-active={isActive}
                          >
                            {link.label}
                          </span>
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </nav>
            </div>
          </div>

          <div className="border-t py-6">
            <div className="flex flex-col items-center justify-between gap-4 sm:flex-row flex-wrap">
              <div className="flex flex-wrap items-center gap-4">
                <p className="text-xs text-muted-foreground" data-testid="text-copyright">
                  &copy; {currentYear} {pillar === "capital" ? "Chief in Tech Capital" : "Chief in Tech LLC"}. All rights reserved.
                </p>
                <nav aria-label="Legal navigation" className="flex gap-4">
                  {legalLinks.slice(0, 3).map((link) => {
                    const legalHref = pillar === "capital"
                      ? `https://www.chiefintech.com${link.href}`
                      : link.href;
                    return (
                    <Link key={link.href} href={legalHref}>
                      <span
                        className="text-xs text-muted-foreground transition-colors hover:text-foreground"
                        data-testid={`link-footer-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                      >
                        {link.label}
                      </span>
                    </Link>
                    );
                  })}
                </nav>
              </div>
              <p className="text-xs text-muted-foreground">
                {pillar === "capital"
                  ? "Pre-seed and seed capital for AI-enabled enterprise systems"
                  : "Empowering women leaders in technology"}
              </p>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
}

interface FooterProps {
  pillar?: Pillar;
  showNewsletter?: boolean;
  showSocialLinks?: boolean;
  showRoleLinks?: boolean;
}

export function Footer({ 
  pillar: pillarOverride,
  showNewsletter = true,
  showSocialLinks = true,
  showRoleLinks = true,
}: FooterProps = {}) {
  const [location] = useLocation();
  const currentYear = new Date().getFullYear();
  const isHomepage = location === "/" || location === "" || location === "/landing";
  const detectedPillar = detectPillar(location);
  const pillar = pillarOverride || detectedPillar;

  // Fetch global brand logo from database
  const { data: productBrands = [] } = useQuery<ProductBrand[]>({
    queryKey: ["/api/brands"],
    staleTime: 24 * 60 * 60 * 1000, // Cache for 24 hours
    gcTime: 24 * 60 * 60 * 1000,
  });

  // Get global brand logo with fallback to hardcoded asset.
  // Prefer the server-injected logo map (always reflects the latest admin
  // upload) so cold-cache visits don't flash the bundled fallback before the
  // brands query resolves.
  const globalBrand = productBrands.find(b => b.productKey === "global");
  const globalLogoUrl =
    getInjectedBrandLogo("global") ||
    globalBrand?.logoUrl ||
    globalBrand?.darkLogoUrl ||
    globalBrand?.iconUrl ||
    logoImage;

  if (isHomepage && !pillarOverride) {
    return <HomepageFooter currentYear={currentYear} globalLogoUrl={globalLogoUrl} />;
  }

  return <StandardFooter currentYear={currentYear} pillar={pillar} globalLogoUrl={globalLogoUrl} />;
}
